import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import {
  ClipboardList, Clock, CheckCircle, AlertTriangle, FileText,
  Plus, Search, Filter, Eye, ChevronRight, TrendingUp, RefreshCw, Copy, Lock, Download
} from "lucide-react";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-slate-100 text-slate-600", dot: "bg-slate-400" },
  in_progress: { label: "In Progress", color: "bg-blue-50 text-blue-700", dot: "bg-blue-500" },
  completed: { label: "Needs Review", color: "bg-amber-50 text-amber-700", dot: "bg-amber-500" },
  reviewed: { label: "Reviewed", color: "bg-purple-50 text-purple-700", dot: "bg-purple-500" },
  finalized: { label: "Finalized", color: "bg-green-50 text-green-700", dot: "bg-green-500" },
};

const TYPE_LABELS = {
  commercial_property: "Commercial Property",
  workers_comp: "Workers' Comp",
  habitational: "Habitational",
  general_liability: "General Liability",
  commercial_auto: "Commercial Auto",
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
      {cfg.label}
    </span>
  );
}

function ExportMenu() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const OPTIONS = [
    { key: "orders",          label: "Orders / Surveys" },
    { key: "findings",        label: "Findings" },
    { key: "recommendations", label: "Recommendations" },
  ];
  const exportCsv = async (dataset) => {
    setBusy(dataset);
    try {
      const res = await api.get(`/carrier/export/${dataset}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `risksurvey_${dataset}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setOpen(false);
    } catch (e) { console.error(e); }
    setBusy("");
  };
  return (
    <div className="relative">
      <button data-testid="export-csv-btn" onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:text-navy hover:border-navy transition-colors duration-150 bg-white">
        <Download className="w-4 h-4" /> Export CSV
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1" data-testid="export-menu">
            {OPTIONS.map(o => (
              <button key={o.key} data-testid={`export-${o.key}-btn`} onClick={() => exportCsv(o.key)} disabled={!!busy}
                className="w-full text-left px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-navy transition-colors flex items-center gap-2 disabled:opacity-50">
                {busy === o.key ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 text-slate-400" />}
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


export default function UnderwriterDashboard() {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.inspection_type = filterType;
      const res = await api.get("/inspections", { params });
      setInspections(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterStatus, filterType]);

  const filtered = inspections.filter(i =>
    !search || i.insured_name.toLowerCase().includes(search.toLowerCase()) ||
    i.property_address?.toLowerCase().includes(search.toLowerCase()) ||
    i.policy_number?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: inspections.length,
    pending: inspections.filter(i => i.status === "pending").length,
    needsReview: inspections.filter(i => i.status === "completed").length,
    finalized: inspections.filter(i => i.status === "finalized").length,
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-800">Inspection Dashboard</h1>
            <p className="text-slate-500 text-sm mt-0.5">Manage and track all loss control surveys</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportMenu />
            <button
              data-testid="order-inspection-btn"
              onClick={() => navigate("/dashboard/order")}
              className="flex items-center gap-2 bg-navy text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors duration-150 shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Order Survey
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total Surveys", value: stats.total, icon: ClipboardList, color: "text-navy", bg: "bg-blue-50" },
            { label: "Pending", value: stats.pending, icon: Clock, color: "text-slate-600", bg: "bg-slate-100" },
            { label: "Needs Review", value: stats.needsReview, icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50" },
            { label: "Finalized", value: stats.finalized, icon: CheckCircle, color: "text-green-600", bg: "bg-green-50" },
          ].map((stat) => (
            <div key={stat.label} className="bg-white border border-slate-200 rounded-lg p-4 stats-card">
              <div className="flex items-center justify-between mb-3">
                <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">{stat.label}</span>
                <div className={`w-8 h-8 ${stat.bg} rounded-lg flex items-center justify-center`}>
                  <stat.icon className={`w-4 h-4 ${stat.color}`} />
                </div>
              </div>
              <div className="font-heading text-3xl font-bold text-slate-800" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g,"-")}`}>{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="search-input"
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search insured, address, or policy..."
                className="w-full pl-9 pr-3 h-9 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors"
              />
            </div>
            <select
              data-testid="filter-status"
              value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="h-9 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 text-slate-600 bg-white"
            >
              <option value="">All Statuses</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select
              data-testid="filter-type"
              value={filterType} onChange={e => setFilterType(e.target.value)}
              className="h-9 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 text-slate-600 bg-white"
            >
              <option value="">All Types</option>
              {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button onClick={load} className="h-9 w-9 flex items-center justify-center border border-slate-200 rounded-lg text-slate-500 hover:text-navy hover:border-navy transition-colors duration-150" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-medium text-slate-700 text-sm">{filtered.length} inspection{filtered.length !== 1 ? "s" : ""}</h2>
          </div>
          {loading ? (
            <div className="py-16 text-center text-slate-400">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              <p className="text-sm">Loading inspections...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No inspections found</p>
              <p className="text-slate-400 text-sm mt-1">Order your first survey to get started</p>
              <button onClick={() => navigate("/dashboard/order")} className="mt-4 bg-navy text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors">Order Survey</button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="inspections-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Insured</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Address</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Ordered</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((insp) => (
                    <tr key={insp.id} className="hover:bg-slate-50 transition-colors duration-100 cursor-pointer" onClick={() => navigate(insp.status === "completed" || insp.status === "reviewed" || insp.status === "finalized" ? `/dashboard/review/${insp.id}` : "#")} data-testid={`inspection-row-${insp.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{insp.insured_name}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{insp.policy_number || "No policy #"}</div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-slate-600">{TYPE_LABELS[insp.inspection_type] || insp.inspection_type}</span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-slate-500">{insp.property_address}</td>
                      <td className="px-4 py-3"><StatusBadge status={insp.status} /></td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-400 text-xs">
                        {new Date(insp.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 justify-end">
                          {(insp.status === "completed" || insp.status === "reviewed" || insp.status === "finalized") && (
                            <button
                              data-testid={`review-btn-${insp.id}`}
                              onClick={e => { e.stopPropagation(); navigate(`/dashboard/review/${insp.id}`); }}
                              className="flex items-center gap-1 px-2.5 py-1 bg-navy/10 text-navy hover:bg-navy hover:text-white rounded-md text-xs font-medium transition-colors duration-150"
                            >
                              <Eye className="w-3 h-3" /> Review
                            </button>
                          )}
                          {insp.status === "finalized" && (
                            <button
                              data-testid={`internal-report-btn-${insp.id}`}
                              onClick={async e => {
                                e.stopPropagation();
                                try {
                                  const res = await api.get(`/reports/${insp.id}/internal-pdf`, { responseType: "blob" });
                                  const url = window.URL.createObjectURL(new Blob([res.data]));
                                  const a = document.createElement("a");
                                  a.href = url;
                                  a.download = `RiskSurvey_INTERNAL_${insp.insured_name?.replace(/\s/g,"_")}.pdf`;
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                } catch (err) { console.error(err); }
                              }}
                              className="flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 hover:bg-red-700 hover:text-white rounded-md text-xs font-medium transition-colors duration-150 border border-red-200"
                            >
                              <Lock className="w-3 h-3" /> Internal
                            </button>
                          )}
                          {(insp.status === "pending" || insp.status === "in_progress") && (
                            <button
                              data-testid={`copy-link-btn-${insp.id}`}
                              onClick={e => {
                                e.stopPropagation();
                                const isForm = insp.mode === "form";
                                const url = `${window.location.origin}/${isForm ? "survey" : "inspection"}/${insp.access_token}`;
                                navigator.clipboard.writeText(url);
                                setCopiedId(insp.id);
                                setTimeout(() => setCopiedId(null), 2000);
                              }}
                              className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-md text-xs font-medium transition-colors duration-150"
                            >
                              {copiedId === insp.id ? (
                                <><CheckCircle className="w-3 h-3 text-green-500" /> Copied!</>
                              ) : (
                                <><Copy className="w-3 h-3" /> Copy Link</>
                              )}
                            </button>
                          )}
                          <ChevronRight className="w-4 h-4 text-slate-300" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
